import * as React from 'react';
import { useMemo } from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import { countryColor } from 'constants/countryColors';
import { ChartData } from 'app/hooks/useChartData';

interface Props {
  territories: ChartData['territoryChart'];
  /** Acciones in the tab: each bar is a share of it. */
  total: number;
}

// Grouped by country along the corridor, west to east, as in the design.
const COUNTRY_ORDER = ['CL', 'AR', 'PY', 'BR'];
const FONT = "'Work Sans', Arial, sans-serif";

const countryRank = (territory: Props['territories'][number]) => {
  const rank = COUNTRY_ORDER.indexOf(territory.countries[0]?.code || '');
  return rank === -1 ? COUNTRY_ORDER.length : rank;
};

// Long names break into lines so they aren't cut.
const wrap = (name: string) => name.toUpperCase().split(' ').reduce<string[]>((lines, word) => {
  const last = lines[lines.length - 1];
  if (last && `${last} ${word}`.length <= 14) {
    lines[lines.length - 1] = `${last} ${word}`;
  } else {
    lines.push(word);
  }
  return lines;
}, []);

/** Share of the tab's Acciones in each Territorio, colored by the Territorio's country. */
const TerritoryBarChart = ({ territories, total }: Props) => {
  const { data, options } = useMemo(() => {
    const sorted = [...territories].sort((a, b) => countryRank(a) - countryRank(b) || a.name.localeCompare(b.name));
    const shares = sorted.map(t => (total ? Math.round((1000 * t.count) / total) / 10 : 0));
    const max = Math.max(60, Math.ceil(Math.max(0, ...shares) / 10) * 10);
    return {
      data: {
        labels: sorted.map(t => wrap(t.name)),
        datasets: [{
          data: shares,
          backgroundColor: sorted.map(t => countryColor(t.countries[0]?.code).fill),
          barPercentage: 0.9,
          categoryPercentage: 0.9
        }]
      },
      options: {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            title: (items: { index: number }[]) => sorted[items[0].index].name,
            label: (item: { index: number }) => `${shares[item.index]}% (${sorted[item.index].count})`
          }
        },
        scales: {
          xAxes: [{ ticks: { min: 0, max, stepSize: 10, fontFamily: FONT, callback: (v: number) => `${v}%` } }],
          yAxes: [{ gridLines: { display: false }, ticks: { fontFamily: "'Montserrat', Arial, sans-serif", fontStyle: 'bold', fontColor: '#383F77', fontSize: 10 } }]
        }
      }
    };
  }, [territories, total]);

  return (
    <div style={{ height: Math.max(240, territories.length * 36) }}>
      <HorizontalBar data={data} options={options} />
    </div>
  );
};

export default TerritoryBarChart;
